import isEqual from 'lodash.isequal';
import { policyType as PolicyType } from '@ourbranch/lookups';

// Auto-containing policy types: anything whose code includes "A". Mirrors the FE check
// (staff-fe/src/customer/components/policy/details/helpers/auto-backdate-violations.js).
const isAutoPolicyType = (policyType?: string): boolean => !!policyType?.includes(PolicyType.Auto);

export const STAFF_AUTO_MAX_BACKDATE_DAYS = 30;

export const ALLOWED_BACKDATED_AUTO_CHANGES = ['add a vehicle', 'remove a vehicle', 'add a driver'] as const;

// Formik registers absent-on-server fields with default `null` on form mount (e.g. a driver's
// schoolName/schoolLocation), producing `undefined` vs `null` deep-equality misses that aren't
// real user changes. Normalize away nullish leaves before comparing so the gate only fires on
// substantive edits. `lodash.isEqualWith` doesn't help here — it short-circuits on differing key
// counts before invoking the customizer per key.
// Fields the FE form recomputes/normalizes on mount via setFieldValue, so the initial-vs-current
// diff registers a "change" the operator never actually made. Strip these from comparison so the
// gate doesn't false-positive on form bookkeeping. Each entry is justified by a concrete useEffect
// in the FE — see staff-fe/src/common/components/personal-injury-protection/pip-coverages/pip-coverages.jsx.
// MI PIP flags that pip-coverages.jsx cascades onto every resident (drivers AND nonDrivers) on
// mount. Shared so a future 4th flag is a one-line edit, not two divergent literals.
const RESIDENT_PIP_KEYS = ['pipOptOut', 'pipHasQHC', 'waivedPIPWL'] as const;

// KNOWN LIMITATION (#30454): car↔driver assignment fields (assignedCar/assignedDriver and the MA
// userAssignedCar/userAssignedDriver) are ignored UNCONDITIONALLY below. This is deliberate but
// imperfect: the gate only sees baseline-vs-submitted state, so it cannot distinguish the FE's
// automatic rebalance after a remove-car (which we MUST allow) from an operator deliberately
// reassigning an existing car to a different driver via the "Edit Car Assignments" modal (which,
// being neither add-car/remove-car/add-driver, should arguably be BLOCKED on a backdated change).
// We accept the rebalance case as dominant and let deliberate reassignment through. The durable
// fix is to compare operator intent (Formik touched/dirty, or an FE-supplied change summary)
// instead of re-deriving the diff server-side and subtracting FE bookkeeping field-by-field —
// tracked in the follow-up issue. This list only needs entries for fields that are IN the GraphQL
// input schema (so they survive sanitize and reach the server in the submission) AND get rewritten
// by an FE mount effect to a value the operator never chose — assignment bookkeeping and the MI PIP
// cascade. Rater-derived / stored-only fields that are NOT in the input schema (riskGroup, eVal,
// excessVehicle, symbol*, msrp, the *Initial deductibles, vehicleHistory*, …) do NOT belong here:
// they're absent from the submission, so the directional comparison in submittedValueMatchesBaseline
// already ignores them — that is the actual root-cause fix for #30454, not a longer ignore list.
const FORM_NORMALIZED_KEYS_BY_PATH: Record<string, Set<string>> = {
  // Michigan PIP coverages: useEffect cascade derives policyLimitPIPACR / PIPWL from PIPME on mount,
  // and re-derives them whenever resident composition changes (which is what happens when a car —
  // and its assigned driver — is removed).
  autoCoverage: new Set(['policyLimitPIPACR', 'policyLimitPIPWL']),
  // Same cascade flips driver-level PIP flags when PIPME changes; happens on mount for any MI policy.
  // Drivers also get assignedCar (default car-assignment rebalance, see default-car-assignment.jsx)
  // and userAssignedCar (MA senior/inexperienced mount-time assignment, see driver.jsx) rewritten by
  // the FE, so neither is a user-intended edit to the existing driver.
  driver: new Set([...RESIDENT_PIP_KEYS, 'assignedCar', 'userAssignedCar']),
  // Non-driver residents get the same MI PIP-flag cascade as drivers via pip-coverages.jsx, but live
  // under the top-level `nonDrivers` key — mirror the driver set so the cascade isn't flagged.
  nonDrivers: new Set([...RESIDENT_PIP_KEYS]),
  // Cars get assignedDriver rebalanced when the FE re-runs driver assignment after a car removal,
  // and userAssignedDriver set during MA mount-time assignment; neither is a user-intended edit to
  // the remaining vehicles. (Note: the field is the singular `assignedDriver`, see
  // default-car-assignment.jsx — an earlier `assignedDrivers` plural typo never matched.)
  car: new Set(['assignedDriver', 'userAssignedDriver'])
};

const stripNullishAndNormalized = (value: unknown, ignoredKeys?: Set<string>): unknown => {
  if (value == null) {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value.map((v) => stripNullishAndNormalized(v, ignoredKeys));
  }
  if (typeof value !== 'object') {
    return value;
  }
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    if (ignoredKeys?.has(k)) {
      continue;
    }
    const normalized = stripNullishAndNormalized(v, ignoredKeys);
    if (normalized !== undefined) {
      result[k] = normalized;
    }
  }
  return result;
};

// Directional deep-compare: does the SUBMITTED (`current`) side introduce any change relative to
// `baseline`? Fields present only in `baseline` are ignored.
//
// Why this must be asymmetric (#30454): the operator's submission reaches the server already
// projected onto the GraphQL input schema (staff-fe sanitize.js PolicyDetailsInput), which strips
// every rater-derived / stored-only field — riskGroup, eVal, excessVehicle, symbol*, msrp, doors,
// engine, the *Initial deductibles, vehicleHistory*, image, etc. The baseline we diff against comes
// from policyDetailsFromSegments(existingPolicy), i.e. the FULL stored car/driver objects with all
// of those fields intact. A symmetric deep-equal therefore flags every one of those ~20 baseline-
// only fields on each surviving car as a "change the operator made", which false-blocked EVERY
// backdated auto endorsement server-side (a remove-car reproduces as "changes to an existing
// vehicle"). Comparing only the fields the operator actually submitted eliminates that entire class
// of false positives and, unlike an allowlist, doesn't rot as new rater-output fields are added.
// (The FE copy of this gate never hit this because it diffs Formik-vs-Formik — both full shape.)
//
// Real edits are still caught: any field the operator changed IS in the submission, so it appears
// in `current` and gets compared. Additions (a key in `current` absent from `baseline`) count as a
// change UNLESS the added value is a falsy scalar (`false`/`''`) — that's the FE's default for a field
// the stored segment never persisted, not an operator edit (see the baseline-absent branch, #33975).
// Removals (a key only in `baseline`) do not — the operator can't have edited a field they never
// round-tripped. Array length changes still count. Both sides are pre-normalized by
// stripNullishAndNormalized (nullish leaves + ignoredKeys removed) before this runs.
const submittedValueMatchesBaseline = (baselineValue: unknown, currentValue: unknown): boolean => {
  if (currentValue === undefined) {
    // Not submitted → cannot be an operator change.
    return true;
  }
  if (baselineValue === undefined) {
    // Submitted but absent from baseline. The FE/GraphQL emits a default `false` (or `''`) for boolean
    // and string fields the stored segment never persisted — e.g. `manualInventorySubmission`, which
    // older auto segments predate, so the FE always sends `false` while the baseline has no such key
    // (#33975: this false-blocked a plain backdated vehicle removal with `changes to "global"`). That
    // is the same Formik/GraphQL default artifact that stripNullishAndNormalized already collapses for
    // `null`, just with a `false`/`''` default instead — not an operator addition. A non-falsy value
    // (`true`, a real string/number, or a populated object/array) IS a genuine addition and still counts.
    return currentValue === false || currentValue === '';
  }
  if (Array.isArray(currentValue) || Array.isArray(baselineValue)) {
    if (!Array.isArray(currentValue) || !Array.isArray(baselineValue) || currentValue.length !== baselineValue.length) {
      return false;
    }
    return currentValue.every((v, i) => submittedValueMatchesBaseline(baselineValue[i], v));
  }
  if (
    typeof currentValue !== 'object' ||
    currentValue === null ||
    typeof baselineValue !== 'object' ||
    baselineValue === null
  ) {
    return isEqual(baselineValue, currentValue);
  }
  // Both objects: every key the operator submitted must match baseline; baseline-only keys ignored.
  return Object.keys(currentValue as Record<string, unknown>).every((k) =>
    submittedValueMatchesBaseline(
      (baselineValue as Record<string, unknown>)[k],
      (currentValue as Record<string, unknown>)[k]
    )
  );
};

// `baseline` is the pre-existing policy state, `current` is what the operator submitted. Asymmetric
// by design — see submittedValueMatchesBaseline.
const isEqualSemantic = (baseline: unknown, current: unknown, ignoredKeys?: Set<string>): boolean =>
  submittedValueMatchesBaseline(
    stripNullishAndNormalized(baseline, ignoredKeys),
    stripNullishAndNormalized(current, ignoredKeys)
  );

const indexBy = <T>(items: T[] | undefined, key: keyof T): Map<unknown, T> => {
  const map = new Map<unknown, T>();
  for (const item of items || []) {
    const k = item?.[key];
    if (k != null) {
      map.set(k, item);
    }
  }
  return map;
};

// Top-level keys we deep-compare. `cars` and `drivers` are handled separately because
// additions/removals are partially allowed. Bookkeeping / derived flags are ignored
// because they don't represent a substantive policy change.
const IGNORED_TOP_LEVEL_KEYS = new Set<string>([
  'cars',
  'drivers',
  'underwritingChanges',
  'fcraDisclaimer',
  'highestEducation',
  'oldestResident',
  // startDate always differs on a backdated submission: the baseline is the merged saved segment
  // (newest segment's startDate) while currentValues carries the operator-picked backdate. The
  // backdate itself is the whole reason this gate runs (caller passes isBackdated), and its
  // magnitude is bounded separately by STAFF_AUTO_MAX_BACKDATE_DAYS — so a startDate diff here is
  // never the "disallowed change" we're looking for.
  'startDate',
  // Bundle "attached"/"include" flags (#30454): these are NOT stored on the segment — the FE derives
  // them fresh on every load from `multiPolicyDiscount` (see staff-fe formatters.js mapDiscountToInputs:
  // attached* = whether multiPolicyDiscount includes A/R/H/C) and always sends them in the submission.
  // The stored baseline usually lacks `attachedAuto` entirely (or carries a stale bool), so the FE's
  // derived `false`/`true` reads as an operator addition/edit that never happened — Dave's preprod
  // block was `changes to "attachedAuto"` with multiPolicyDiscount unchanged at 'N'. Ignoring them is
  // safe because their single source of truth, `multiPolicyDiscount`, is NOT ignored: a real bundle
  // change moves that field and is still blocked. `includeRenters`/`includeUmbrella` likewise drive a
  // top-level coverage object (rentersCoverage/umbrellaCoverage) that stays gated. The BE's own
  // segments.manager compressSegmentValues already strips this same set as normalized bookkeeping.
  'attachedAuto',
  'attachedHomeowners',
  'attachedRenters',
  'attachedCondo',
  'includeRenters',
  'includeUmbrella'
]);

const findChangedTopLevelField = (
  initialValues: Record<string, unknown> | null | undefined,
  currentValues: Record<string, unknown> | null | undefined
): string | null => {
  const keys = new Set([...Object.keys(initialValues || {}), ...Object.keys(currentValues || {})]);
  for (const key of keys) {
    if (IGNORED_TOP_LEVEL_KEYS.has(key)) {
      continue;
    }
    if (!isEqualSemantic(initialValues?.[key], currentValues?.[key], FORM_NORMALIZED_KEYS_BY_PATH[key])) {
      return key;
    }
  }
  return null;
};

type SegmentLike = {
  cars?: Array<Record<string, unknown> & { VIN?: string }>;
  drivers?: Array<Record<string, unknown> & { id?: string }>;
} & Record<string, unknown>;

/**
 * Determine whether the pending change set on an auto-containing policy includes anything
 * other than the three change types we permit to be backdated (add car, remove car, add driver).
 *
 * Returns a human-readable description of the disallowed change, or null if every change is allowed.
 * Modifications to existing cars, modifications to existing drivers, removals of drivers, and
 * any change to coverage / non-vehicle / non-driver fields are all considered disallowed.
 */
export const getDisallowedAutoBackdateChange = (
  initialValues: SegmentLike | null | undefined,
  currentValues: SegmentLike | null | undefined
): string | null => {
  if (!initialValues || !currentValues) {
    return null;
  }

  const initialCarsByVin = indexBy(initialValues.cars, 'VIN');
  const currentCarsByVin = indexBy(currentValues.cars, 'VIN');
  for (const [vin, currentCar] of currentCarsByVin) {
    const initialCar = initialCarsByVin.get(vin);
    if (initialCar && !isEqualSemantic(initialCar, currentCar, FORM_NORMALIZED_KEYS_BY_PATH.car)) {
      return 'changes to an existing vehicle';
    }
  }

  const initialDriversById = indexBy(initialValues.drivers, 'id');
  const currentDriversById = indexBy(currentValues.drivers, 'id');
  for (const id of initialDriversById.keys()) {
    if (!currentDriversById.has(id)) {
      return 'removing a driver';
    }
  }
  for (const [id, currentDriver] of currentDriversById) {
    const initialDriver = initialDriversById.get(id);
    if (initialDriver && !isEqualSemantic(initialDriver, currentDriver, FORM_NORMALIZED_KEYS_BY_PATH.driver)) {
      return 'changes to an existing driver';
    }
  }

  const changedField = findChangedTopLevelField(initialValues, currentValues);
  if (changedField) {
    return `changes to "${changedField}"`;
  }

  return null;
};

export type AutoBackdateCheckResult = { allowed: true } | { allowed: false; reason: string };

export const isAutoBackdateChangeAllowed = ({
  policyType,
  isBackdated,
  initialValues,
  currentValues
}: {
  policyType?: string;
  isBackdated: boolean;
  initialValues?: SegmentLike | null;
  currentValues?: SegmentLike | null;
}): AutoBackdateCheckResult => {
  if (!isBackdated) {
    return { allowed: true };
  }
  if (!isAutoPolicyType(policyType)) {
    return { allowed: true };
  }
  const disallowed = getDisallowedAutoBackdateChange(initialValues, currentValues);
  if (!disallowed) {
    return { allowed: true };
  }
  return {
    allowed: false,
    reason: `Backdated auto endorsements are limited to ${ALLOWED_BACKDATED_AUTO_CHANGES.join(', ')}. This change includes ${disallowed}.`
  };
};
